import React, {FC, useState} from 'react'
import {i18n} from '../../translate/i18n'

// Reaproveita tipos e helpers
type ChangeHandler<T = any> = (value: T) => void
export type Option = {value: string | number; label: string}
const toSelectOpts = (opts: Option[]) => opts.map((o) => ({value: o.value, select: o.label}))

// Preview lock (mesma ideia do original)
export const PreviewLock: React.FC<React.PropsWithChildren> = ({children}) => (
  <div style={{pointerEvents: 'none', userSelect: 'none'}} aria-hidden tabIndex={-1}>
    {children}
  </div>
)

// -----------------------------------------------------------------------------
// 1) Title (apenas exibição / label grande)
// -----------------------------------------------------------------------------
export const TitleBox: FC<{value?: string; size?: number}> = ({value = '', size = 5}) => {
  const Tag = `h${Math.min(Math.max(size, 1), 6)}` as unknown as keyof JSX.IntrinsicElements
  return (
    <div className='mb-2'>
      <Tag className='m-0'>{value || i18n.t('service_requests.title_placeholder')}</Tag>
    </div>
  )
}

// -----------------------------------------------------------------------------
// 2) Text input
// -----------------------------------------------------------------------------
type TextInputBoxProps = {
  value?: string
  onChange?: ChangeHandler<string>
  label?: string
  placeholder?: string
  disabled?: boolean
  idInput?: string
}
export const TextInputBox: FC<TextInputBoxProps> = ({
  value = '',
  onChange,
  label = i18n.t('service_requests.text_label'),
  placeholder = i18n.t('service_requests.text_placeholder'),
  disabled,
  idInput,
}) => (
  <div className='form-group'>
    <label htmlFor={idInput}>{label}</label>
    <input
      id={idInput}
      className='form-control'
      type='text'
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
    />
  </div>
)

// -----------------------------------------------------------------------------
// 3) Textarea
// -----------------------------------------------------------------------------
type TextareaBoxProps = {
  value?: string
  onChange?: ChangeHandler<string>
  label?: string
  rows?: number
  placeholder?: string
  disabled?: boolean
  idInput?: string
}
export const TextareaBox: FC<TextareaBoxProps> = ({
  value = '',
  onChange,
  label = i18n.t('service_requests.textarea_label'),
  rows = 3,
  placeholder = i18n.t('service_requests.textarea_placeholder'),
  disabled,
  idInput,
}) => (
  <div className='form-group'>
    <label htmlFor={idInput}>{label}</label>
    <textarea
      id={idInput}
      className='form-control'
      rows={rows}
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
    />
  </div>
)

// -----------------------------------------------------------------------------
// 4) Checkbox set (lista de opções)
// -----------------------------------------------------------------------------
type CheckboxBoxProps = {
  items?: Option[]
  value?: Array<string | number>
  onChange?: ChangeHandler<Array<string | number>>
  label?: string
  disabled?: boolean
}
export const CheckboxBox: FC<CheckboxBoxProps> = ({
  items = [],
  value = [],
  onChange,
  label = i18n.t('service_requests.checkbox_label'),
  disabled,
}) => {
  const toggle = (v: string | number) => {
    const exists = value.includes(v)
    const next = exists ? value.filter((x) => x !== v) : [...value, v]
    onChange?.(next)
  }

  return (
    <div className='form-group'>
      <label className='d-block mb-2'>{label}</label>
      <div className='d-grid gap-2'>
        {items.map((it) => (
          <label key={String(it.value)} className='form-check'>
            <input
              className='form-check-input'
              type='checkbox'
              checked={value.includes(it.value)}
              onChange={() => toggle(it.value)}
              disabled={disabled}
            />
            <span className='form-check-label'>{it.label}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// 5) Radio group
// -----------------------------------------------------------------------------
type RadioBoxProps = {
  items?: Option[]
  value?: string | number
  onChange?: ChangeHandler<string | number>
  label?: string
  name?: string
  disabled?: boolean
}
export const RadioBox: FC<RadioBoxProps> = ({
  items = [],
  value,
  onChange,
  label = i18n.t('service_requests.radio_label'),
  name,
  disabled,
}) => {
  const groupName = name ?? `radio-${Math.random().toString(36).slice(2, 8)}`
  return (
    <div className='form-group'>
      <label className='d-block mb-2'>{label}</label>
      <div className='d-grid gap-2'>
        {items.map((it) => (
          <label key={String(it.value)} className='form-check'>
            <input
              className='form-check-input'
              type='radio'
              name={groupName}
              checked={String(value) === String(it.value)}
              onChange={() => onChange?.(it.value)}
              disabled={disabled}
            />
            <span className='form-check-label'>{it.label}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// 6) Select (reutiliza helper)
// -----------------------------------------------------------------------------
type SelectBoxProps = {
  value?: string | number
  onChange?: ChangeHandler<string | number>
  items?: Option[]
  label?: string
  placeholder?: string
  disabled?: boolean
}
export const SelectBox: FC<SelectBoxProps> = ({
  value,
  onChange,
  items = [],
  label = i18n.t('service_requests.select_list_label'),
  placeholder = i18n.t('service_requests.select_item_placeholder'),
  disabled,
}) => (
  <div className='form-group'>
    <label className='d-block mb-2'>{label}</label>
    <select
      className='form-select'
      value={value ?? ''}
      onChange={(e) => onChange?.(e.target.value)}
      disabled={disabled}
    >
      <option value=''>{placeholder}</option>
      {items.map((it) => (
        <option key={String(it.value)} value={it.value}>
          {it.label}
        </option>
      ))}
    </select>
  </div>
)

// -----------------------------------------------------------------------------
// 7) Stars (avaliação)
// -----------------------------------------------------------------------------
type StarsBoxProps = {
  value?: number
  onChange?: ChangeHandler<number>
  max?: number
  label?: string
  disabled?: boolean
}
export const StarsBox: FC<StarsBoxProps> = ({value = 0, onChange, max = 5, label}) => {
  const [hover, setHover] = useState<number | null>(null)
  const current = value
  return (
    <div className='form-group'>
      {label && <label className='d-block mb-2'>{label}</label>}
      <div>
        {Array.from({length: max}, (_, i) => {
          const idx = i + 1
          const filled = hover !== null ? idx <= hover : idx <= current
          return (
            <button
              key={idx}
              type='button'
              aria-label={`${idx} stars`}
              className='btn btn-link p-0 me-1'
              onMouseEnter={() => setHover(idx)}
              onMouseLeave={() => setHover(null)}
              onClick={() => onChange?.(idx)}
              style={{textDecoration: 'none', fontSize: 20}}
            >
              {filled ? '★' : '☆'}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Sample options and catalog (previews com PreviewLock)
// -----------------------------------------------------------------------------
const SAMPLE_ITEMS: Option[] = [
  {value: 'opt1', label: i18n.t('service_requests.sample_service_1')},
  {value: 'opt2', label: i18n.t('service_requests.sample_service_2')},
]

export type FormBlockKindExtra =
  | 'title'
  | 'text'
  | 'textarea'
  | 'checkbox'
  | 'radio'
  | 'select'
  | 'stars'

export type FormBlockMeta = {
  id: FormBlockKindExtra
  title: string
  icon?: string
  getPreview: () => JSX.Element
}

export const FORMS_CATALOG_EXTRA: FormBlockMeta[] = [
  {
    id: 'title',
    title: i18n.t('service_requests.catalog_title'),
    icon: 'bi-type',
    getPreview: () => (
      <PreviewLock>
        <TitleBox value={i18n.t('service_requests.sample_title')} />
      </PreviewLock>
    ),
  },
  {
    id: 'text',
    title: i18n.t('service_requests.catalog_text'),
    icon: 'bi-fonts',
    getPreview: () => (
      <PreviewLock>
        <TextInputBox value={i18n.t('service_requests.sample_text')} onChange={() => {}} />
      </PreviewLock>
    ),
  },
  {
    id: 'textarea',
    title: i18n.t('service_requests.catalog_textarea'),
    icon: 'bi-card-text',
    getPreview: () => (
      <PreviewLock>
        <TextareaBox value={i18n.t('service_requests.sample_text')} onChange={() => {}} rows={3} />
      </PreviewLock>
    ),
  },
  {
    id: 'checkbox',
    title: i18n.t('service_requests.catalog_checkbox'),
    icon: 'bi-check2-square',
    getPreview: () => (
      <PreviewLock>
        <CheckboxBox items={SAMPLE_ITEMS} value={[]} onChange={() => {}} />
      </PreviewLock>
    ),
  },
  {
    id: 'radio',
    title: i18n.t('service_requests.catalog_radio'),
    icon: 'bi-record-circle',
    getPreview: () => (
      <PreviewLock>
        <RadioBox items={SAMPLE_ITEMS} value={undefined} onChange={() => {}} />
      </PreviewLock>
    ),
  },
  {
    id: 'select',
    title: i18n.t('service_requests.catalog_select'),
    icon: 'bi-list',
    getPreview: () => (
      <PreviewLock>
        <SelectBox items={SAMPLE_ITEMS} value={undefined} onChange={() => {}} />
      </PreviewLock>
    ),
  },
  {
    id: 'stars',
    title: i18n.t('service_requests.catalog_stars'),
    icon: 'bi-star',
    getPreview: () => (
      <PreviewLock>
        <StarsBox value={4} onChange={() => {}} />
      </PreviewLock>
    ),
  },
]

export default {
  TitleBox,
  TextInputBox,
  TextareaBox,
  CheckboxBox,
  RadioBox,
  SelectBox,
  StarsBox,
  FORMS_CATALOG_EXTRA,
}
